import { useState } from "react";
import { EntityType } from "@common/Common.types.ts";
import { ITaskRelationsBox } from "./TaskRelationsBox";
import { exhaustiveGuard } from "@common/utilities.ts";
import { LegalEntityAuditTabs } from "../../../legalEntity/LegalEntity.types.ts";
import { RiskAreaTab } from "../../../risk/riskArea/RiskArea.types.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { useRelationsQuery, useRemoveRelation } from "@common/hooks/Relations.hooks.ts";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { TaskRelationViewModel } from "./TaskRelationsBox.types.ts";

export default function useTaskRelationsBoxHooks(props: ITaskRelationsBox) {
    const [expandedByUser, setExpandedByUser] = useState<boolean>();
    const { platformFeatures } = usePlanContext();

    const { isLoading, data, refetch } = useRelationsQuery(props.taskId);
    const { removeRelation: removeRelationFromTask } = useRemoveRelation(props.taskId, data);

    const parentActivityRelationsUrl = props.annualWheelActivityId ? `/relations/${props.annualWheelActivityId}/` : "";
    const { isLoading: parentActivityRelationsIsLoading, data: parentActivityRelationsRaw } = useDotLegalQuery<Array<TaskRelationViewModel>>(
        parentActivityRelationsUrl,
        {
            enabled: !!props.annualWheelActivityId,
        }
    );

    const parentActivityRelations = parentActivityRelationsRaw
        ?.filter((x) => x.entityType !== EntityType.User)
        ?.filter((x) => !data?.some((taskRelation) => taskRelation.entityId === x.entityId));

    const hasRelations = (data?.length ?? 0) > 0 || (parentActivityRelations?.length ?? 0) > 0;

    // Until the user clicks the section it follows the data and opens itself once the relations have loaded.
    // From the first click on, the user's choice is the only thing that decides. Deriving it instead of
    // pushing it into state through an effect is what keeps a re-render from re-opening what was just closed.
    const expanded = expandedByUser ?? (!isLoading && !parentActivityRelationsIsLoading && platformFeatures.canAccessRelationsBetweenEntities && hasRelations);

    async function removeRelation(id: string) {
        // Taking the last relation away would otherwise flip the derived default back to collapsed while the
        // user is still working in the open section, so the removal counts as the user keeping it open.
        setExpandedByUser(true);
        await removeRelationFromTask(id);
    }

    return { expanded, setExpanded: setExpandedByUser, data, removeRelation, refetch, parentActivityRelations };
}

export function getLink(
    type: EntityType,
    id: string,
    parentId: string | undefined,
    urlProvider: ReturnType<typeof useUrlProvider>,
    subjectEntityType?: EntityType,
    subjectEntityId?: string
) {
    switch (type) {
        case EntityType.IncidentLog:
            return urlProvider.getIncidentLogOverviewUrl(id);
        case EntityType.Process:
            return urlProvider.getProcessOverviewUrl(id);
        case EntityType.Policy:
            return urlProvider.getPolicyUrl(id);
        case EntityType.LegalEntity:
            return urlProvider.getLegalEntityUrl(id);
        case EntityType.ProcessingActivity:
            return urlProvider.getProcessingActivityOverviewUrl(id);
        case EntityType.Risk:
            return getRiskAssessmentLink(parentId, urlProvider, subjectEntityType, subjectEntityId);
        case EntityType.System:
            return urlProvider.getSystemOverviewUrl(id);
        case EntityType.AnnualWheelActivity:
            return urlProvider.getAnnualWheelUrlWithActivityId(id);
        case EntityType.Task:
            return urlProvider.getTaskManagementTaskDialogUrl(id);
        case EntityType.LegalEntityAuditGroup:
            return urlProvider.getAuditGroupUrl(id);
        case EntityType.LegalEntityAudit:
            return parentId ? urlProvider.getLegalEntityUrl(parentId, LegalEntityAuditTabs.Audits) : urlProvider.getExternalLegalEntities();
        case EntityType.LegalEntityClassification:
            return parentId ? urlProvider.getLegalEntityUrl(parentId, LegalEntityAuditTabs.Classifications) : urlProvider.getExternalLegalEntities();
        case EntityType.LegalEntityDocument:
            return parentId ? urlProvider.getLegalEntityUrl(parentId, LegalEntityAuditTabs.Documents) : urlProvider.getExternalLegalEntities();
        // An internal assessment has no page of its own, so it links to the group that sent it. One sent
        // outside a group has no parent and falls back to the list, which is where it is shown.
        case EntityType.InternalAssessment:
            return parentId ? urlProvider.getInternalAssessmentGroupUrl(parentId) : urlProvider.getInternalAssessmentUrl();
        case EntityType.InternalAssessmentGroup:
            return urlProvider.getInternalAssessmentGroupUrl(id);
        case EntityType.Contract:
            return urlProvider.getContractOverviewUrl(id);
        case EntityType.User:
            return urlProvider.getUserUrl(id);
        case EntityType.ContractDocument:
            return parentId ? urlProvider.getContractOverviewUrl(parentId) : urlProvider.getContractsUrl();
        case EntityType.FrameworkControl:
            return urlProvider.getFrameworkControlOverviewUrl(id);
        case EntityType.ContractType:
        case EntityType.BusinessArea:
        case EntityType.EntityTemplate:
        case EntityType.PaymentTerm:
        case EntityType.Tag:
        case EntityType.IncidentLogDocument:
        case EntityType.ProcessDocument:
        case EntityType.TaskDocument:
        case EntityType.SystemDocument:
        case EntityType.PolicyDocument:
        case EntityType.ProcessingActivityDocument:
        case EntityType.DocumentType:
        case EntityType.ComplianceArea:
        case EntityType.DeletionPeriod:
        case EntityType.Certification:
        case EntityType.SecurityMeasures:
        case EntityType.DataCategory:
        case EntityType.DataSubject:
        case EntityType.LegalBasis:
        case EntityType.Organisation:
            return "";
        default:
            exhaustiveGuard(type);
    }
}

function getRiskAssessmentLink(
    riskAreaId: string | undefined,
    urlProvider: ReturnType<typeof useUrlProvider>,
    subjectEntityType: EntityType | undefined,
    subjectEntityId: string | undefined
) {
    if (!riskAreaId) {
        return "";
    }

    // The backend sends null rather than leaving the subject out, hence the loose comparison.
    if (subjectEntityType != null && subjectEntityId) {
        return urlProvider.getRiskAssessmentsOverviewUrl(riskAreaId, subjectEntityType, subjectEntityId);
    }

    // An organisation-level assessment has no page of its own, it is shown on the risk area's governance tab.
    // A mention only stores the risk area, so it lands there too.
    return urlProvider.getRiskAreaUrl(riskAreaId, RiskAreaTab.Governance);
}
